// @system — Consolidated auth provider (cookie sessions).
//
// Single source of truth for authentication. The server issues an httpOnly
// access_token + refresh_token cookie pair from POST /api/sessions; the browser
// never sees a bearer token. Every call goes through lib/@system/api.js, which
// sends `credentials: 'include'`, attaches the CSRF token to mutations and
// transparently replays a request once after POST /api/sessions/refresh.
//
//   mount     → GET    /api/sessions/me        → { user | null }
//   login     → POST   /api/sessions           → { user }   (sets cookies)
//   register  → POST   /api/sessions/register  → { user }   (sets cookies)
//   logout    → DELETE /api/sessions           (blacklists + revokes cookies)
//
// The previous implementation kept a bearer token in localStorage and skipped
// the session check when none was stored, so cookie-authenticated users always
// appeared logged out. Do not reintroduce localStorage tokens here.
//
// To extend: import { AuthContext, useAuthContext } and build @custom hooks on top.
import { createContext, useState, useEffect, useCallback, useContext, useMemo } from 'react'
import { api } from '@/app/lib/@system/api'
import { isServerRendered } from '@/app/lib/@system/initialState'

/**
 * The signed-in user as the session endpoints return it (toPublicUser on the
 * server) plus the legacy plan fields some @custom backends decorate it with.
 * @typedef {import('@/app/api/@system/index.js').PublicUser & {
 *   plan?: string | null,
 *   subscription?: { plan?: string | null, status?: string } | null,
 * }} AuthUser
 */

/**
 * `POST /sessions` / `/sessions/register` / `GET /sessions/me` body. Extra
 * members (e.g. an MFA challenge) may accompany or replace `user`.
 * @typedef {{ user?: AuthUser | null } & Record<string, unknown>} SessionResponse
 */

/** Extra body fields forwarded to the login/register call (TOTP code, invite token…). */
/** @typedef {Record<string, unknown>} AuthExtras */

/**
 * @typedef {Object} AuthContextValue
 * @property {AuthUser | null} user
 * @property {StateSetter<AuthUser | null>} setUser
 * @property {boolean} loading True until the mount-time session check settles
 * @property {boolean} isAuthenticated
 * @property {(email: string, password: string, opts?: AuthExtras) => Promise<SessionResponse>} login
 * @property {(email: string, password: string, name?: string, opts?: AuthExtras) => Promise<SessionResponse>} register
 * @property {() => Promise<void>} logout
 * @property {() => Promise<AuthUser | null>} refresh Re-read the session (after profile edits / verification)
 * @property {(url: string, options?: import('@/app/lib/@system/api').ApiRequestOptions) => Promise<Response>} authFetch
 *   Legacy: same-origin `fetch` with cookies; resolves the raw Response
 * @property {null} token Always null — cookie auth has no client-readable token
 */

export const AuthContext = createContext(/** @type {AuthContextValue | null} */ (null))

/** @param {WithChildren} props */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(/** @type {AuthUser | null} */ (null))
  // Server-rendered documents: the server rendered the page as a signed-out visitor with
  // loading=false (GuestRoute shows the sign-in form, not a spinner). Start from the same state
  // so the hydrating render matches the HTML; the mount effect below then resolves the session
  // and a signed-in user is redirected/updated exactly as before. Plain shell: loading=true.
  const [loading, setLoading] = useState(() => !isServerRendered())

  // Re-read the current session (e.g. after profile edits or email verification).
  const refresh = useCallback(async () => {
    try {
      /** @type {SessionResponse | null} */
      const data = await api.get('/sessions/me')
      const next = data?.user ?? null
      setUser(next)
      return next
    } catch {
      setUser(null)
      return null
    }
  }, [])

  // Session check on mount — cookies are sent automatically.
  useEffect(() => {
    let cancelled = false
    api
      .get('/sessions/me')
      .then((/** @type {SessionResponse | null} */ data) => { if (!cancelled) setUser(data?.user ?? null) })
      .catch(() => { if (!cancelled) setUser(null) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const login = useCallback(
    /**
     * @param {string} email
     * @param {string} password
     * @param {AuthExtras} [opts]
     * @returns {Promise<SessionResponse>}
     */
    async (email, password, opts = {}) => {
      /** @type {SessionResponse} */
      const data = await api.post('/sessions', { email, password, ...opts })
      if (data?.user) setUser(data.user)
      return data
    }, [])

  const register = useCallback(
    /**
     * @param {string} email
     * @param {string} password
     * @param {string} [name]
     * @param {AuthExtras} [opts]
     * @returns {Promise<SessionResponse>}
     */
    async (email, password, name, opts = {}) => {
      /** @type {SessionResponse} */
      const data = await api.post('/sessions/register', { email, password, name, ...opts })
      if (data?.user) setUser(data.user)
      return data
    }, [])

  const logout = useCallback(async () => {
    try {
      await api.delete('/sessions')
    } catch {
      // Cookies may already be gone — clearing local state is what matters.
    }
    setUser(null)
  }, [])

  // Legacy helper for callers that still fetch raw URLs: same-origin request
  // with cookies. Prefer `api` from lib/@system/api for new code.
  const authFetch = useCallback(
    /**
     * @param {string} url
     * @param {import('@/app/lib/@system/api').ApiRequestOptions} [options]
     */
    (url, options = {}) =>
      fetch(url, {
        credentials: 'include',
        ...options,
        headers: { 'Content-Type': 'application/json', ...options.headers },
      }),
    [],
  )

  /** @type {AuthContextValue} */
  const value = useMemo(
    () => ({
      user,
      setUser,
      loading,
      isAuthenticated: !!user,
      login,
      register,
      logout,
      refresh,
      authFetch,
      // Kept for API compatibility with older @custom code; cookie auth has no
      // client-readable token.
      token: null,
    }),
    [user, loading, login, register, logout, refresh, authFetch],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * useAuthContext — the main hook for all components
 * @returns {AuthContextValue}
 */
export function useAuthContext() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuthContext must be used inside <AuthProvider>')
  return ctx
}

// Alias for convenience
export const useAuth = useAuthContext
